import { useState } from 'react';
import { ApiError, api } from '@/lib/api/client';

/**
 * Porte d'entrée de l'application.
 *
 * Vérifié le 9 septembre 2026 depuis l'Internet public : sans authentification,
 * `/api/leads/export.csv` livrait toute la base — noms, adresses, téléphones et
 * emails d'entreprises françaises — et `POST /api/jobs` permettait à n'importe
 * qui de consommer le quota de 30 000 appels RapidAPI.
 *
 * Le message d'erreur est celui de l'API, volontairement identique pour un
 * compte inconnu et un mot de passe faux : distinguer les deux permettrait
 * d'énumérer les comptes.
 */
export function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(event: React.FormEvent) {
    event.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api('/auth/login', { method: 'POST', body: { email, password } });
      onSuccess();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.detail : 'connexion impossible');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-sunken p-6">
      <form
        onSubmit={soumettre}
        className="w-full max-w-sm space-y-5 rounded-xl bg-surface p-8 shadow-sm"
      >
        <div className="space-y-1">
          <h1 className="text-lg font-semibold">Bakiscrap</h1>
          <p className="text-sm text-secondary">Connexion requise pour continuer.</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="email" className="block text-sm font-medium">
            Adresse email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-subtle bg-surface px-3 py-2 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="block text-sm font-medium">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-subtle bg-surface px-3 py-2 text-sm"
          />
        </div>

        {erreur ? (
          <p role="alert" className="text-sm text-danger">
            {erreur}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={enCours}
          className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {enCours ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </main>
  );
}
